import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api-client";

export interface BriefingPriority {
  id: string;
  type: "deal" | "task" | "lead" | "alert";
  title: string;
  description: string;
  actionLabel: string;
  actionUrl: string;
  urgency: "high" | "medium" | "low";
}

interface TodayIntelligence {
  closingToday: BriefingPriority[];
  followUp: BriefingPriority[];
  newLeads: BriefingPriority[];
  overdue: BriefingPriority[];
}

function flattenIntelligence(intel: TodayIntelligence): BriefingPriority[] {
  return [
    ...intel.closingToday,
    ...intel.overdue,
    ...intel.followUp,
    ...intel.newLeads,
  ];
}

function isServerFailure(error: unknown): boolean {
  return (
    error !== null
    && typeof error === "object"
    && "status" in error
    && typeof (error as { status?: unknown }).status === "number"
    && (error as { status: number }).status >= 500
  );
}

export function useBriefingPriorities() {
  const [data, setData] = useState<BriefingPriority[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ data: TodayIntelligence }>("/crm/intelligence/today")
      .then((res) => {
        const intel = res.data;
        setData(intel ? flattenIntelligence(intel) : []);
      })
      .catch((err) => {
        if (isServerFailure(err)) {
          setData([]);
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { data, isLoading, error };
}
